import { useState } from "react";
import {
  DEFAULT_DOCUMENT_CATEGORY,
  DOCUMENT_TYPES,
  ENTITY_TYPES,
} from "@miplatform/constants";
import { useBoolean } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import { externalReferenceId } from "@miplatform/utils";
import { useDocumentsViewModel } from "@miplatform/view-models";

export const useCloseoutDocumentsViewModel = ({
  home,
  orderTemplate,
  getHome,
}) => {
  const {
    loading,
    uploadDocument,
    removeDocument,
    getDocumentCategories,
    downloadFile,
    printFile,
  } = useDocumentsViewModel();
  const [documentCategory, setDocumentCategory] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [deleteFile, setDeleteFile] = useState(null);
  const [isDeleteModalOpen, handleDeleteModalOpen, handleDeleteModalClose] =
    useBoolean();

  const setDeleteDocument = (document) => {
    setDeleteFile(document);
    handleDeleteModalOpen();
  };

  const handleDocumentSelect = (document) => {
    setSelectedDocument(document);
  };

  const handleResetSelectedDocument = () => {
    setSelectedDocument(null);
  };

  const getDocumentCategoryOptions = async () => {
    const categories = await getDocumentCategories(ENTITY_TYPES.HOME);

    if (categories) {
      setDocumentCategory(
        categories.find((cat) => cat.name === DEFAULT_DOCUMENT_CATEGORY) ||
          categories[0]
      );
    }
  };

  const uploadFiles = async (files) => {
    if (!files || files?.length === 0 || loading) return;

    const invalidFile = files.find((file) => file.error);

    if (invalidFile) {
      toastService.error({
        text: "File Upload Failed",
        subtext: invalidFile.error || "Please check your files and try again.",
      });
      return;
    }

    const externalData = {
      orderTemplateId: "None",
      tradeId: orderTemplate?.tradeId,
      orderTemplateName: "None",
      tradeName: orderTemplate?.tradeName,
    };
    const encodedData = encodeURIComponent(JSON.stringify(externalData));
    const uploadPromises = files.map((file) =>
      uploadDocument({
        file,
        documentType: DOCUMENT_TYPES.UNKNOWN,
        category: documentCategory?.name,
        externalData: encodedData,
        externalReferenceId: externalReferenceId.getHomeRefId({
          homeId: home.id,
        }),
      })
    );
    const results = await Promise.all(uploadPromises);

    if (results.some((res) => res === null)) {
      toastService.error({
        text: "Upload Document Failed.",
      });
    } else {
      toastService.success({
        text: "Uploaded Document Successfully.",
      });
    }

    getHome(home.id);
  };

  const deleteDocument = async () => {
    const res = await removeDocument({
      id: deleteFile?.id,
      modifiedOn: deleteFile?.modifiedOn,
    });

    if (res) {
      toastService.success({
        text: "Deleted Document Successfully.",
      });
      getHome(home.id);
      handleDeleteModalClose();
    }
  };

  return {
    loading,
    documentCategory,
    isDeleteModalOpen,
    deleteFile,
    selectedDocument,
    getDocumentCategoryOptions,
    downloadFile,
    printFile,
    uploadFiles,
    deleteDocument,
    setDeleteDocument,
    handleDeleteModalClose,
    handleDocumentSelect,
    handleResetSelectedDocument,
  };
};
